import { useEffect, useRef } from 'react';
import type { Customer, HealthState, Resource, Site } from '@itr/shared';
import { api } from '../api/client';
import { useRealtimeEvent } from '../api/realtime';
import { playAlertChime } from './chime';
import { isAlertSpeechEnabled, speakHealthTransition } from './speak';

interface AlertWatch {
  siteIds: string[];
  resourceIds: string[];
}

export interface AlertTrigger {
  resourceId: string;
  name: string;
  customerName: string | null;
  siteName: string | null;
  health: string;
  previousHealth: string;
  ts: number;
}

/** Alerts this tab has already sounded, so a replayed frame doesn't repeat it. */
const sounded = new Set<string>();

/**
 * Every open tab (and the desktop shell) gets the same frame, and each would
 * chime and speak it — one alert read out once per window. A Web Lock named
 * after the event lets exactly one of them take it; the rest stay quiet. The
 * lock is held a while so a tab whose frame lands late still finds it taken.
 */
async function claimAlert(key: string): Promise<boolean> {
  if (sounded.has(key)) return false;
  sounded.add(key);
  if (sounded.size > 200) sounded.delete(sounded.values().next().value as string);
  if (typeof navigator === 'undefined' || !navigator.locks) return true;
  try {
    return await new Promise<boolean>((resolve) => {
      void navigator.locks.request(`itr.alert:${key}`, { ifAvailable: true }, (lock) => {
        resolve(lock !== null);
        return lock ? new Promise((done) => window.setTimeout(done, 30_000)) : undefined;
      });
    });
  } catch {
    return true;
  }
}

/**
 * Plays a chime when a resource the user is watching (directly, or via its
 * site) changes health while this tab is open — the bonus path alongside
 * real push notifications (notifications/push.ts), which are what actually
 * reaches a closed tab/browser.
 *
 * A resource's `siteId`/`name` aren't on the realtime event itself, so a
 * lookup is built once and refreshed periodically rather than re-fetched per
 * event. `onTrigger`, when given, fires alongside the chime so the header
 * bell (Shell.tsx) can surface the same event visually.
 */
export function useAlertSound(onTrigger?: (trigger: AlertTrigger) => void): void {
  const watchRef = useRef<AlertWatch>({ siteIds: [], resourceIds: [] });
  const infoByResourceRef = useRef<
    Map<string, { siteId: string | null; name: string; customerName: string | null; siteName: string | null }>
  >(new Map());
  const onTriggerRef = useRef(onTrigger);
  onTriggerRef.current = onTrigger;

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const [me, resources, customers, sites] = await Promise.all([
          api.get<{ alertWatch?: AlertWatch }>('/api/auth/me'),
          api.get<Resource[]>('/api/resources', { limit: 500 }),
          // Names only — an alarm still fires without them if either lookup fails.
          api.get<Customer[]>('/api/customers').catch(() => [] as Customer[]),
          api.get<Site[]>('/api/sites').catch(() => [] as Site[]),
        ]);
        if (cancelled) return;
        watchRef.current = me.alertWatch ?? { siteIds: [], resourceIds: [] };
        const customerNames = new Map(customers.map((c) => [c.id, c.name]));
        const siteById = new Map(sites.map((s) => [s.id, s]));
        infoByResourceRef.current = new Map(
          resources.map((r) => {
            const site = r.siteId ? siteById.get(r.siteId) : undefined;
            const customerId = r.customerId ?? site?.customerId ?? null;
            return [
              r.id,
              {
                siteId: r.siteId,
                name: r.name,
                customerName: customerId ? customerNames.get(customerId) ?? null : null,
                siteName: site?.name ?? null,
              },
            ];
          }),
        );
      } catch {
        // Best-effort: the chime just stays quiet until the next refresh.
      }
    };

    void load();
    // Watch-list edits and new resources are rare; a periodic refresh keeps
    // this current without a dedicated invalidation event for what is a
    // secondary, best-effort path (the real notification is the push).
    const timer = window.setInterval(() => void load(), 5 * 60_000);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, []);

  useRealtimeEvent(['resources'], (event) => {
    if (event.type !== 'resource.updated') return;
    if (event.health === event.previousHealth) return;
    const watch = watchRef.current;
    const info = infoByResourceRef.current.get(event.resourceId);
    const watching = watch.resourceIds.includes(event.resourceId) || (!!info?.siteId && watch.siteIds.includes(info.siteId));
    if (!watching) return;
    const health = event.health as HealthState;
    const name = info?.name ?? event.resourceId;
    const customerName = info?.customerName ?? null;
    const siteName = info?.siteName ?? null;
    // `up` is the one transition that means "cleared" — everything else is a
    // fresh or worsening problem, so the chime and the spoken word both key
    // off the same distinction.
    void claimAlert(`${event.resourceId}:${event.health}:${event.ts}`).then((mine) => {
      if (!mine) return;
      playAlertChime(health === 'up' ? 'up' : 'down');
      if (isAlertSpeechEnabled()) speakHealthTransition(name, health, customerName, siteName);
    });
    onTriggerRef.current?.({
      resourceId: event.resourceId,
      name,
      customerName,
      siteName,
      health: event.health,
      previousHealth: event.previousHealth,
      ts: event.ts,
    });
  });
}
